/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

/**
 * Filters the given "srcMap", returning a new Map that only includes the key/value pairs which
 * return a truthy value when passed to the given "filterFunc".
 *
 * @param srcMap The map to filter
 * @param filterFunc The filtering function that determines which key/value pairs to include in the result
 * @returns The resulting filtered Map
 */
export function filterMap<K, V>(srcMap: Map<K, V>, filterFunc: (key: K, value: V) => boolean): Map<K, V> {
	const result = new Map<K, V>();
	for (const [key, value] of srcMap.entries()) {
		if (filterFunc(key, value)) {
			result.set(key, value);
		}
	}
	return result;
}

/**
 * Basically the same as [[filterMap]] above, but operates on a map Iterator instead of a map
 * @param mapIterator An IterableIterator of key, value tuples. What you get when called aMap.entries().
 * ```
 * const filteredMap = new Map(filterMapIterator(mapIterable, ([key,value]) => true)));
 * ```
 */
export function* filterMapIterator<T>(
	mapIterator: IterableIterator<T> | null,
	filterFunc: (entry: T) => boolean
): IterableIterator<T> {
	if (!mapIterator) {
		return;
	} //ensure an IterableIterator is still returned
	for (const comb of mapIterator) {
		if (filterFunc(comb)) {
			yield comb;
		}
	}
}
